import { useCallback, useState } from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { type ConversationCatalogItemComponent } from '../../value-objects/ConversationTurnItem'

const DEFAULT_QUANTITY = 1

export interface UseCatalogItemCartResult {
  selectedItem: Nullable<ConversationCatalogItemComponent>
  quantity: number
  estimatedTotal: number
  selectCatalogItem: (item: ConversationCatalogItemComponent) => void
  collapse: () => void
  changeQuantity: (quantity: number) => void
  addToRequest: () => void
  quantityOf: (item: ConversationCatalogItemComponent) => number
  changeItemQuantity: (
    item: ConversationCatalogItemComponent,
    quantity: number
  ) => void
  addItemToRequest: (item: ConversationCatalogItemComponent) => void
}

export const useCatalogItemCart = (
  sendMessage: (message: string) => boolean
): UseCatalogItemCartResult => {
  const intl = useProcurifyIntl()
  const [selectedItem, setSelectedItem] =
    useState<Nullable<ConversationCatalogItemComponent>>(null)
  const [quantitiesById, setQuantitiesById] = useState<Record<string, number>>(
    {}
  )

  const quantityOf = useCallback(
    (item: ConversationCatalogItemComponent) =>
      quantitiesById[item.itemId] ?? item.quantity ?? DEFAULT_QUANTITY,
    [quantitiesById]
  )

  const quantity = selectedItem ? quantityOf(selectedItem) : DEFAULT_QUANTITY
  const estimatedTotal = selectedItem ? selectedItem.price * quantity : 0

  const selectCatalogItem = useCallback(
    (item: ConversationCatalogItemComponent) => setSelectedItem(item),
    []
  )

  const collapse = useCallback(() => setSelectedItem(null), [])

  const changeItemQuantity = useCallback(
    (item: ConversationCatalogItemComponent, next: number) => {
      setQuantitiesById((prev) => ({ ...prev, [item.itemId]: next }))
    },
    []
  )

  const changeQuantity = useCallback(
    (next: number) => {
      if (!selectedItem) return
      changeItemQuantity(selectedItem, next)
    },
    [changeItemQuantity, selectedItem]
  )

  const addItemToRequest = useCallback(
    (item: ConversationCatalogItemComponent) => {
      const didSend = sendMessage(
        intl.formatMessage(messages.addToRequest, {
          title: item.title,
          quantity: quantityOf(item),
        })
      )
      if (didSend) setSelectedItem(null)
    },
    [intl, quantityOf, sendMessage]
  )

  const addToRequest = useCallback(() => {
    if (!selectedItem) return
    addItemToRequest(selectedItem)
  }, [addItemToRequest, selectedItem])

  return {
    selectedItem,
    quantity,
    estimatedTotal,
    selectCatalogItem,
    collapse,
    changeQuantity,
    addToRequest,
    quantityOf,
    changeItemQuantity,
    addItemToRequest,
  }
}
